import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import {
  PATH_CARD_SMALL_HEIGHT,
  PATH_CARD_SMALL_WIDTH,
} from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard.constants";
import type { VariantType } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardTypes";

import type { Learnin } from "../../../../helpers/learnins";

export interface BucketOfLearninRowProps {
  loading: boolean;
  name: string;
  description?: string;
  id: number | string;
  learnins?: Learnin[] | null;
  variant?: VariantType;
  pathType?: string;
  viewAllHref?: string;
  completed?: boolean;
}

const CARD_GAP = 24;

// Returns a callback ref + the live column count. Callback ref (instead of useEffect +
// useRef) so the observer wires up the instant the grid div actually mounts — even when
// earlier renders returned null (e.g. while quests/paths are still loading and the row's
// own empty-list early-return bails out before attaching the div). With useEffect, the
// effect would only run once at mount with `ref.current === null`, then never again.
const useVisibleCardCount = () => {
  const [cols, setCols] = useState(1);
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const update = (width: number) => {
      const nextCols = Math.max(
        1,
        Math.floor((width + CARD_GAP) / (PATH_CARD_SMALL_WIDTH + CARD_GAP))
      );
      setCols(nextCols);
    };

    update(node.clientWidth);

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) update(entry.contentRect.width);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [cols, containerRef] as const;
};

export const BucketOfLearninRow = ({
  loading,
  name,
  description,
  id,
  learnins,
  variant,
  pathType,
  viewAllHref,
  completed = false,
}: BucketOfLearninRowProps) => {
  const navigate = useNavigate();
  const [cols, gridRef] = useVisibleCardCount();

  const count = learnins?.length ?? 0;

  // `loading` is authoritative — callers may pass an empty array while a request is
  // in-flight (e.g. when state initializes to []), so don't gate skeleton rendering on
  // `learnins === undefined`. Only hide the row entirely when we know we're settled and
  // empty.
  if (!loading && count === 0) {
    return null;
  }

  const visibleLearnins = learnins?.slice(0, cols);
  const hasOverflow = count > cols;

  return (
    <div className="mb-6">
      <h2 className={"atlas-h5 pb-2 flex items-center gap-2"}>
        {name}
        {completed && (
          <>
            <i
              className="pi pi-check-circle"
              style={{ color: "var(--success-color)" }}
              aria-hidden="true"
            />
            <span className="sr-only">(completed)</span>
          </>
        )}
      </h2>
      {description && <p>{description}</p>}
      <div ref={gridRef} className="flex flex-nowrap gap-6 overflow-hidden py-6">
        {loading ? (
          <>
            {Array.from({ length: cols }, (_, i) => `skelly-${i}`).map(skel => (
              <Skeleton
                key={`${id}-${skel}`}
                width={`${PATH_CARD_SMALL_WIDTH}px`}
                height={`${PATH_CARD_SMALL_HEIGHT}px`}
              />
            ))}
          </>
        ) : (
          <>
            {visibleLearnins?.map(learnin => (
              <PathCard
                key={learnin.id}
                cardSize="small"
                variant={variant}
                id={String(learnin.id)}
                name={learnin.name}
                description={learnin.description}
                pathType={pathType}
                status={learnin.status}
                enrolled={learnin.enrolled}
              />
            ))}
          </>
        )}
      </div>
      {!loading && viewAllHref && hasOverflow && (
        <div className="flex justify-end">
          <Button
            link
            icon="pi pi-arrow-right"
            iconPos="right"
            label={`Show all (${count})`}
            onClick={() => navigate(viewAllHref)}
            aria-label={`Show all ${count} in ${name}`}
          />
        </div>
      )}
    </div>
  );
};
